// Version: 2.14
// v2.14: Code digit boxes take their look from .mfa-digit in Login.css only (CV-50).
// v2.13: Buttons use the shared button system (CV-49): 8px corners, standard variants and sizes, one selected look; no inline radius, padding or font size.
import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from './auth';
import './Login.css';

export default function MFAVerify() {
  const navigate = useNavigate();
  const location = useLocation();
  const { loginComplete } = useAuth();

  const { pre_token, username, email_hint } = location.state || {};

  const [code, setCode]         = useState(['','','','','','']);
  const [error, setError]       = useState('');
  const [loading, setLoading]   = useState(false);
  const [sending, setSending]   = useState(false);
  const [sent, setSent]         = useState(false);
  const [countdown, setCountdown] = useState(600); // 10 min
  const inputRefs = useRef([]);

  useEffect(() => {
    if (!pre_token) { navigate('/login'); return; }
    // Auto-send OTP on mount
    sendCode();
  }, []);

  useEffect(() => {
    if (!sent) return;
    const timer = setInterval(() => setCountdown(c => {
      if (c <= 1) { clearInterval(timer); return 0; }
      return c - 1;
    }), 1000);
    return () => clearInterval(timer);
  }, [sent]);

  const sendCode = async () => {
    setSending(true); setError('');
    try {
      const res = await fetch('/api/auth/mfa/send', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ pre_token })
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'Failed to send code');
      setSent(true); setCountdown(600);
    } catch (err) {
      setError(err.message);
    } finally {
      setSending(false);
    }
  };

  const handleDigit = (idx, val) => {
    if (!/^\d?$/.test(val)) return;
    const next = [...code];
    next[idx] = val;
    setCode(next);
    if (val && idx < 5) inputRefs.current[idx + 1]?.focus();
    if (next.every(d => d !== '')) submitCode(next.join(''));
  };

  const handleKeyDown = (idx, e) => {
    if (e.key === 'Backspace' && !code[idx] && idx > 0) {
      inputRefs.current[idx - 1]?.focus();
    }
  };

  const handlePaste = (e) => {
    const pasted = e.clipboardData.getData('text').replace(/\D/g, '').slice(0, 6);
    if (pasted.length === 6) {
      setCode(pasted.split(''));
      submitCode(pasted);
    }
  };

  const submitCode = async (fullCode) => {
    setLoading(true); setError('');
    try {
      const res = await fetch('/api/auth/mfa/verify', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ pre_token, code: fullCode }),
        credentials: 'include'
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'Verification failed');
      loginComplete(data);
      // Explicit navigation after MFA — App.js re-render may be delayed
      if (data.password_change_required) {
        navigate('/change-password');
      } else if (data.role === 'client' && data.client_slug) {
        navigate('/clients/' + data.client_slug);
      } else {
        navigate('/dashboard');
      }
      // Navigate based on role and password_change_required
      if (data.password_change_required) {
        navigate('/change-password');
      } else if (data.role === 'client' && data.client_slug) {
        navigate(`/clients/${data.client_slug}`);
      } else {
        navigate('/dashboard');
      }
    } catch (err) {
      setError(err.message);
      setCode(['','','','','','']);
      inputRefs.current[0]?.focus();
    } finally {
      setLoading(false);
    }
  };

  const formatTime = (s) => `${Math.floor(s/60)}:${String(s%60).padStart(2,'0')}`;

  return (
    <div className="login-wrap">
      <div className="login-card">
        <div className="login-header">
          <div className="login-logo">
            <span className="login-logo-stg">STG</span>
            <span className="login-logo-sep" />
            <span className="login-logo-sub">Assessment Portal</span>
          </div>
          <div className="login-tagline">Two-step verification</div>
        </div>
        <div className="login-body">
          <p style={{fontSize:13,color:'var(--text-muted)',marginBottom:24}}>
            {sent ? <>A 6-digit code has been sent to <strong style={{color:'var(--text)'}}>{email_hint || username}</strong>. Enter it below.</> : 'Sending your verification code...'}
          </p>

          <div className="mfa-digits" onPaste={handlePaste}>
            {code.map((d, i) => (
              <input
                key={i}
                ref={el => inputRefs.current[i] = el}
                type="text"
                inputMode="numeric"
                maxLength={1}
                value={d}
                onChange={e => handleDigit(i, e.target.value)}
                onKeyDown={e => handleKeyDown(i, e)}
                className="mfa-digit"
                disabled={loading}
                autoFocus={i === 0}
              />
            ))}
          </div>

          {error && <div className="error-msg" style={{marginTop:16}}>{error}</div>}

          {loading && <div style={{textAlign:'center',marginTop:16}}><span className="spinner" /></div>}

          <div style={{marginTop:20,display:'flex',alignItems:'center',justifyContent:'space-between',fontSize:12,color:'var(--text-muted)'}}>
            <span>{countdown > 0 ? <>Code expires in <strong style={{fontFamily:'var(--font-mono)'}}>{formatTime(countdown)}</strong></> : 'Code expired'}</span>
            <button className="btn btn-secondary btn-sm"
             
             
              onClick={sendCode}
              disabled={sending || countdown > 540}
            >
              {sending ? <span className="spinner" /> : 'Resend code'}
            </button>
          </div>

          <button className="btn btn-secondary btn-block" style={{ marginTop: 16 }}
           
           
            onClick={() => navigate('/login')}
          >
            ← Back to sign in
          </button>
        </div>
      </div>
    </div>
  );
}